import { useContextSnackbar } from 'components/Snackbar/context';
import DataGrid from 'devextreme-react/data-grid';
import { DataGridCell, exportDataGrid } from 'devextreme/excel_exporter';
import { exportDataGrid as exportDataGridPdf, PdfDataGridCell } from 'devextreme/pdf_exporter';
import dxDataGrid from 'devextreme/ui/data_grid';
import { Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { encodeMacRoman, worksheetToCsv } from 'utils/csv';
import { filenameDatetimeFormat } from 'utils/dateFormat';

export type DataGridRef = React.LegacyRef<DataGrid<unknown, string>>;
type TableRef = { current: { instance?: dxDataGrid<unknown, string> } };

export interface BaseExportOptions {
  tableRef: DataGridRef;
  fileName?: string;
  addDateTimeToFileName?: boolean;
}

export interface PdfCell {
  content: string | number;
  styles: Record<string, string | number>;
}

export interface ExcelCell {
  value: string | number;
  row?: number;
  // No type provided by the lib, but feel free to add more fields here when needed.
}

export interface PdfExportOptions extends BaseExportOptions {
  pageTitle?: string;
  orientation?: 'portrait' | 'landscape';
  action: 'open' | 'download';
  customizeCellFn?: (gridCell?: PdfDataGridCell, pdfCell?: PdfCell) => void;
}

export interface ExcelExportOptions extends BaseExportOptions {
  sheetName?: string;
  customizeCellFn?: (gridCell?: DataGridCell, excelCell?: ExcelCell) => void;
}

export interface CsvExportOptions extends BaseExportOptions {
  customizeCellFn?: (gridCell?: DataGridCell, excelCell?: ExcelCell) => void;
}

const generateFileName = (fileName?: string, addDateTimeToFileName?: boolean) => {
  return addDateTimeToFileName
    ? fileName
      ? `${fileName}_${filenameDatetimeFormat(new Date())}`
      : `DataGrid_${filenameDatetimeFormat(new Date())}`
    : fileName ?? 'DataGrid';
};

const exportToWorksheet = (
  tableRef: DataGridRef,
  worksheet: Worksheet,
  customizeCellFn?: ExcelExportOptions['customizeCellFn'],
) => {
  if (!(tableRef as TableRef)?.current?.instance) {
    throw new Error('No DataGrid instance found for exporting');
  }

  return exportDataGrid({
    component: (tableRef as TableRef).current.instance,
    worksheet,
    autoFilterEnabled: true,
    customizeCell: ({ gridCell, excelCell }) => {
      // row numbers are not part of the data, so we need to set them manually
      if (gridCell?.column?.caption === '#' && gridCell.rowType === 'data') {
        excelCell.value = excelCell.row - 1;
      }
      if (
        gridCell?.column?.caption === 'UMSATZ (OHNE SOLL/HABEN-KZ)' &&
        gridCell.rowType === 'data'
      ) {
        const valueNumber = Number(gridCell.value);
        excelCell.value = valueNumber;
        excelCell.numFmt = '#,##0.00'; // neutral format
      }
      if (customizeCellFn) {
        customizeCellFn(gridCell, excelCell);
      }
    },
  });
};

export const useDataGridExporter = () => {
  const { t } = useTranslation();
  const { showError } = useContextSnackbar();

  const exportExcel = (options: ExcelExportOptions) => {
    const { tableRef, fileName, sheetName, addDateTimeToFileName, customizeCellFn } = options;
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(sheetName ?? 'Main sheet');
    const finalFilename = generateFileName(fileName, addDateTimeToFileName);

    return exportToWorksheet(tableRef, worksheet, customizeCellFn)
      .then(() => {
        workbook.xlsx
          .writeBuffer()
          .then((buffer) => {
            saveAs(
              new Blob([buffer], { type: 'application/octet-stream' }),
              `${finalFilename}.xlsx`,
            );
          })
          .catch((e) => {
            throw e;
          });
      })
      .catch((err) => {
        console.error('Excel export error', err);
        showError(t('error_unableToExportDocument'));
      });
  };

  const exportCsv = (options: CsvExportOptions) => {
    const { tableRef, fileName, addDateTimeToFileName, customizeCellFn } = options;
    const worksheet = new Workbook().addWorksheet();
    const finalFilename = generateFileName(fileName, addDateTimeToFileName);

    return exportToWorksheet(tableRef, worksheet, customizeCellFn)
      .then(() => {
        const csv = encodeMacRoman(worksheetToCsv(worksheet));
        saveAs(new Blob([csv], { type: 'text/csv' }), `${finalFilename}.csv`);
      })
      .catch((err) => {
        console.error('CSV export error', err);
        showError(t('error_unableToExportDocument'));
      });
  };

  const exportPdf = (options: PdfExportOptions) => {
    const {
      tableRef,
      orientation,
      action,
      fileName,
      pageTitle,
      addDateTimeToFileName,
      customizeCellFn,
    } = options;
    if (!(tableRef as TableRef)?.current?.instance) {
      throw new Error('No DataGrid instance found for exporting');
    }
    const doc = new jsPDF({
      orientation: orientation ?? 'portrait',
    });
    const finalFilename = generateFileName(fileName, addDateTimeToFileName);

    let rowNumber = 1;

    return exportDataGridPdf({
      jsPDFDocument: doc,
      component: (tableRef as TableRef).current.instance,
      keepColumnWidths: true,
      customizeCell: ({ gridCell, pdfCell }) => {
        // row numbers are not part of the data, so we need to set them manually
        if (gridCell?.column?.caption === '#' && gridCell.rowType === 'data') {
          pdfCell.content = rowNumber++;
        }
        if (customizeCellFn) {
          customizeCellFn(gridCell, pdfCell);
        }
      },
      autoTableOptions: {
        tableLineWidth: 0,
        headStyles: { fillColor: [230, 230, 230], textColor: [120, 120, 120] },
        horizontalPageBreak: true,
        horizontalPageBreakRepeat: 0,
        didDrawPage: () => {
          doc.setFontSize(12);
          doc.text(pageTitle ?? '', 14, 12);
        },
      },
    })
      .then(() => {
        switch (action) {
          case 'download':
            doc.save(`${finalFilename}.pdf`);
            break;
          case 'open':
            doc.output('dataurlnewwindow', { filename: `${finalFilename}.pdf` });
            break;
        }
      })
      .catch((err) => {
        console.error('PDF export error', err);
        showError(t('error_unableToExportDocument'));
      });
  };

  return {
    exportExcel,
    exportCsv,
    exportPdf,
  };
};
